import { ZapFastIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import { DeleteModal } from '@/components/Delete';
import { RuleLogsPane } from '@/components/Rules/RuleLogsPane';
import { Button } from '@/design-system';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useOrganizationId, useRunRules } from '@/hooks/useSwr';
import { RuleSchema } from '@/types/schema';
import { SESSION_RULES_DOCS, apiRunsPath } from '@/utils/constants';
import { xCount } from '@/utils/stringUtils';

import EmptyState from '../EmptyState';
import { getRulesColumnsDef } from './components/Columns';
import { useRulesTable } from './hooks/useRulesTable';

export const SessionRulesTable = ({
  onCreateRule,
  onRuleSelect,
}: {
  onCreateRule: () => void;
  onRuleSelect?: (rule: RuleSchema) => void;
}) => {
  const { sessionId } = useParams();

  const {
    data: allRules,
    mutate,
    isLoading,
    isValidating,
  } = useRunRules({
    session_id: sessionId,
  });

  // Filter out evaluator rules - only show rules that are NOT evaluators
  const nonEvaluatorRules = useMemo(() => {
    return allRules?.filter(
      (rule) =>
        (rule.evaluators?.length ?? 0) === 0 &&
        (rule.code_evaluators?.length ?? 0) === 0
    );
  }, [allRules]);

  const [ruleLogsPaneRule, setRuleLogsPaneRule] = useState<
    RuleSchema | undefined
  >(undefined);

  if (!allRules && isLoading) {
    return <LinearProgress />;
  }

  if (!sessionId) {
    return <div>Session ID is required</div>;
  }

  return (
    <>
      <RulesTable
        rules={nonEvaluatorRules ?? []}
        isLoading={isLoading}
        isValidating={isValidating}
        mutate={mutate}
        setRuleLogsPaneRule={setRuleLogsPaneRule}
        onRowClick={(rule) => {
          onRuleSelect?.(rule);
        }}
        onCreateRule={onCreateRule}
      />
      <RuleLogsPane
        rule={ruleLogsPaneRule}
        open={!!ruleLogsPaneRule}
        onClose={() => setRuleLogsPaneRule(undefined)}
      />
    </>
  );
};

const RulesTable = (props: {
  rules: RuleSchema[];
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
  setRuleLogsPaneRule: Dispatch<SetStateAction<RuleSchema | undefined>>;
  onRowClick: (rule: RuleSchema) => void;
  onCreateRule: () => void;
}) => {
  'use no memo';
  const { paginationModel, setPaginationModel } = useDataGridState({});
  const [deleteOpen, setDeleteOpen] = useState<string | null>(null);

  // Implement client-side pagination since the API doesn't support pagination
  const paginatedRules = useMemo(() => {
    if (!props.rules) return undefined;

    const startIndex = paginationModel.pageIndex * paginationModel.pageSize;
    const endIndex = startIndex + paginationModel.pageSize;
    const slicedRules = props.rules.slice(startIndex, endIndex);

    // Add headers property for pagination information
    return Object.assign(slicedRules, {
      headers: {
        'x-pagination-total': props.rules.length,
      },
    });
  }, [props.rules, paginationModel.pageIndex, paginationModel.pageSize]);

  const totalCount = props.rules?.length ?? 0;

  const organizationId = useOrganizationId();

  const columns = useMemo(() => {
    return getRulesColumnsDef({
      organizationId,
      onOpenRuleLogs: props.setRuleLogsPaneRule,
    });
  }, [organizationId, props.setRuleLogsPaneRule]);

  const table = useRulesTable({
    rows: paginatedRules,
    paginationModel,
    onPaginationChange: setPaginationModel,
    columns,
    setDeleteOpen,
  });

  if (props.rules.length === 0) {
    return (
      <EmptyState
        title="Create your first automation"
        description={
          <div>
            There are no automation rules configured for this project.
            Automation rules allow you to trigger actions such as adding traces
            to a dataset, or triggering a webhook on your trace data.
          </div>
        }
        action={
          <Button size="md" onClick={props.onCreateRule}>
            Create Automation
          </Button>
        }
        docsLink={SESSION_RULES_DOCS}
        fancyIcon={false}
        icon={<ZapFastIcon />}
        className="mt-12"
        size="md"
      />
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-secondary">
        <DataGrid
          table={table}
          cellHeight={56}
          cellSpacingVariant="wide"
          isLoading={props.isLoading || props.isValidating}
          onClick={props.onRowClick}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-tertiary">
          {totalCount > 0 && `${xCount('rule', totalCount)} total`}
        </div>
        <DataGridPagination table={table} />
      </div>

      <DeleteModal
        endpoint={`${apiRunsPath}/rules`}
        id={deleteOpen as string}
        isOpen={!!deleteOpen}
        doClose={() => {
          props.mutate();
          setDeleteOpen(null);
        }}
        invalidationPrefixes={[`${apiRunsPath}/rules`]}
        modalText="Are you sure you want to delete this rule?"
      />
    </>
  );
};
